import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import {
  FileText, DollarSign, Clock, CheckCircle2, AlertCircle, CreditCard, Building2,
  Upload, Copy, AlertTriangle, Loader2, Landmark, Hash, ArrowLeft, Calendar
} from "lucide-react";

const statusConfig = {
  pending: { label: "Due", color: "bg-amber-100 text-amber-700" },
  overdue: { label: "Past Due", color: "bg-red-100 text-red-700" },
  paid: { label: "Paid", color: "bg-emerald-100 text-emerald-700" },
  cancelled: { label: "Cancelled", color: "bg-slate-100 text-slate-500" },
};

function CopyBtn({ text, label }) {
  return (
    <button type="button" className="p-0.5 hover:bg-slate-200 rounded ml-1 inline-flex" onClick={() => { navigator.clipboard.writeText(text); toast.success(`${label} copied`); }}>
      <Copy className="h-3 w-3 text-slate-400" />
    </button>
  );
}

export default function InvoiceDetailPage() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmation, setConfirmation] = useState(null);

  // Reference submission state
  const [refNumber, setRefNumber] = useState("");
  const [depositDate, setDepositDate] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchInvoice = useCallback(async () => {
    try {
      const res = await apiClient.get("/invoices");
      const found = res.data.find(inv => inv.id === invoiceId);
      if (found) {
        setInvoice(found);
        // Fetch deposit confirmation if pending/overdue
        if (found.status === "pending" || found.status === "overdue") {
          try {
            const confRes = await apiClient.get(`/invoices/${found.id}/deposit-confirmation`);
            if (confRes.data.length > 0) setConfirmation(confRes.data[0]);
          } catch {}
        }
      }
    } catch {
      toast.error("Failed to load invoice");
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => { fetchInvoice(); }, [fetchInvoice]);

  const handleSubmitReference = async () => {
    if (!refNumber.trim() || !depositDate) {
      toast.error("Please enter a reference number and deposit date");
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("reference_number", refNumber.trim());
      formData.append("deposit_date", depositDate);
      formData.append("notes", notes.trim());
      if (uploadFile) formData.append("file", uploadFile);

      const res = await apiClient.post(`/invoices/${invoiceId}/deposit-confirmation`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(res.data.message || "Payment reference submitted!");
      setRefNumber(""); setDepositDate(""); setUploadFile(null); setNotes("");
      fetchInvoice();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayNow = async () => {
    if (!invoice) return;
    try {
      const res = await apiClient.post(`/invoices/${invoice.id}/pay`, {
        payment_method: "card", origin_url: window.location.origin,
      });
      if (res.data.processor === "fiserv" && res.data.gateway_url && res.data.form_params) {
        const form = document.createElement("form");
        form.method = "POST"; form.action = res.data.gateway_url;
        Object.entries(res.data.form_params).forEach(([k, v]) => {
          const input = document.createElement("input");
          input.type = "hidden"; input.name = k; input.value = v; form.appendChild(input);
        });
        document.body.appendChild(form); form.submit();
      } else if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment initiation failed");
    }
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-4 border-sag-navy border-t-transparent rounded-full" /></div></DashboardLayout>;

  if (!invoice) return (
    <DashboardLayout>
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <AlertCircle className="h-12 w-12 text-slate-300" />
        <p className="text-slate-500">Invoice not found</p>
        <Button variant="outline" onClick={() => navigate("/client/invoices")}>Go to My Invoices</Button>
      </div>
    </DashboardLayout>
  );

  const today = new Date().toISOString().split("T")[0];
  const isDueToday = invoice.due_date === today;
  const isPastDue = invoice.due_date < today && invoice.status !== "paid";
  const statusInfo = isPastDue && invoice.status === "pending"
    ? { label: "Past Due", color: "bg-red-100 text-red-700" }
    : isDueToday && invoice.status === "pending"
    ? { label: "Due Today", color: "bg-orange-100 text-orange-700" }
    : statusConfig[invoice.status] || statusConfig.pending;

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="invoice-detail-page">
        {/* Back Button */}
        <Button variant="ghost" size="sm" onClick={() => navigate("/client/invoices")} className="text-slate-500">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to My Invoices
        </Button>

        {/* Invoice Header Card */}
        <Card className="sag-card overflow-hidden">
          {(isPastDue || isDueToday) && invoice.status !== "paid" && (
            <div className={`px-6 py-3 ${isPastDue ? "bg-red-600" : "bg-amber-500"} text-white font-semibold text-center text-sm`} data-testid="invoice-urgency-banner">
              {isPastDue ? `OVERDUE — This invoice was due on ${new Date(invoice.due_date + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}` : "PAYMENT DUE TODAY"}
            </div>
          )}
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-mono text-xl font-bold text-sag-navy" data-testid="invoice-number">{invoice.invoice_number}</p>
                <p className="text-sm text-slate-500">{invoice.client_name}</p>
              </div>
              <Badge className={`text-sm px-4 py-1.5 ${statusInfo.color}`} data-testid="invoice-status-badge">
                {statusInfo.label}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* Key Details */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-50 rounded-xl p-4" data-testid="invoice-amount">
                <p className="text-xs text-slate-400 uppercase font-medium">Amount Due</p>
                <p className="font-bold text-2xl text-sag-navy mt-1">${invoice.total.toLocaleString()}</p>
                <p className="text-xs text-slate-400">{invoice.currency}</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4" data-testid="invoice-due-date">
                <p className="text-xs text-slate-400 uppercase font-medium">Due Date</p>
                <p className={`font-semibold text-lg mt-1 ${isPastDue ? "text-red-600" : isDueToday ? "text-orange-600" : ""}`}>
                  {new Date(invoice.due_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-xs text-slate-400 uppercase font-medium">Billing Period</p>
                <p className="font-medium text-sm mt-1">{invoice.billing_period_start} to {invoice.billing_period_end}</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-xs text-slate-400 uppercase font-medium">Payment Method</p>
                <p className="font-medium text-sm mt-1 capitalize flex items-center gap-1">
                  {invoice.payment_method === "bank_transfer" ? <Building2 className="h-3.5 w-3.5" /> : <CreditCard className="h-3.5 w-3.5" />}
                  {(invoice.payment_method || "card/deposit").replace("_", " ")}
                </p>
              </div>
            </div>

            {/* Line Items */}
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase mb-2">Line Items</p>
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                {invoice.items.map((item, i) => (
                  <div key={`item-${i}`} className="flex items-center justify-between p-4 border-b border-slate-100 last:border-0">
                    <div><p className="text-sm font-medium">{item.description}</p><p className="text-xs text-slate-500">{item.quantity} x ${item.unit_price.toLocaleString()}</p></div>
                    <p className="font-semibold">${item.total.toLocaleString()}</p>
                  </div>
                ))}
                <div className="flex items-center justify-between p-4 bg-slate-50 font-bold text-lg">
                  <span>Total Due</span>
                  <span className="text-sag-navy">${invoice.total.toLocaleString()} {invoice.currency}</span>
                </div>
              </div>
            </div>

            {invoice.notes && <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3">{invoice.notes}</p>}

            {/* Paid Confirmation */}
            {invoice.status === "paid" && invoice.manual_payment && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="paid-confirmation">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  <p className="font-semibold text-emerald-800">Payment Confirmed</p>
                </div>
                <div className="text-sm text-emerald-700 space-y-1">
                  <p>Reference: {invoice.manual_payment.reference_number}</p>
                  <p>Bank: {invoice.manual_payment.bank_name}</p>
                  <p>Received: {invoice.manual_payment.date_received}</p>
                </div>
              </div>
            )}

            {/* Deposit Confirmation Status */}
            {confirmation && (
              <div className={`rounded-xl p-4 border ${confirmation.status === "approved" ? "bg-emerald-50 border-emerald-200" : confirmation.status === "rejected" ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`} data-testid="deposit-confirmation-status">
                <p className="font-semibold text-sm mb-1">
                  {confirmation.status === "pending_review" && "Payment reference submitted — awaiting verification"}
                  {confirmation.status === "approved" && "Payment reference verified"}
                  {confirmation.status === "rejected" && "Payment reference rejected — please re-submit"}
                </p>
                <p className="text-xs">Ref: {confirmation.reference_number} | Date: {confirmation.deposit_date}</p>
                {confirmation.review_notes && <p className="text-xs mt-1">Admin notes: {confirmation.review_notes}</p>}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payment Section — only for unpaid invoices */}
        {(invoice.status === "pending" || invoice.status === "overdue") && (
          <>
            {/* Online Payment */}
            {invoice.payment_method !== "bank_transfer" && (
              <Card className="sag-card">
                <CardHeader><CardTitle className="text-lg flex items-center gap-2"><CreditCard className="h-5 w-5" /> Pay Online</CardTitle></CardHeader>
                <CardContent>
                  <Button className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-lg font-semibold" onClick={handlePayNow} data-testid="pay-online-btn">
                    <CreditCard className="h-5 w-5 mr-2" /> Pay ${invoice.total.toLocaleString()} Now
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Bank Transfer Instructions */}
            <Card className="sag-card" data-testid="bank-details-card">
              <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Landmark className="h-5 w-5" /> Direct Deposit Payment Instructions</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {/* Bahamas */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3"><Landmark className="h-4 w-4 text-blue-700" /><p className="font-semibold text-blue-800 text-sm">Bahamas Clients — Scotiabank</p></div>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between"><span className="text-blue-600">Bank</span><span className="font-semibold text-blue-900">Scotiabank Bahamas</span></div>
                    <div className="flex justify-between"><span className="text-blue-600">Account Name</span><span className="font-semibold text-blue-900">Sebastian Alliance Group Ltd.</span></div>
                    <div className="flex justify-between"><span className="text-blue-600">Account #</span><span className="font-mono font-semibold text-blue-900">171287<CopyBtn text="171287" label="Account number" /></span></div>
                    <div className="flex justify-between"><span className="text-blue-600">Branch</span><span className="font-mono font-semibold text-blue-900">03465 (Carmichael)<CopyBtn text="03465" label="Branch number" /></span></div>
                  </div>
                </div>
                {/* US */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3"><Landmark className="h-4 w-4 text-emerald-700" /><p className="font-semibold text-emerald-800 text-sm">U.S. Clients — Wells Fargo</p></div>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between"><span className="text-emerald-600">Bank</span><span className="font-semibold text-emerald-900">Wells Fargo</span></div>
                    <div className="flex justify-between"><span className="text-emerald-600">Account Name</span><span className="font-semibold text-emerald-900">Sebastian Alliance Group LLC</span></div>
                    <div className="flex justify-between"><span className="text-emerald-600">Account #</span><span className="font-mono font-semibold text-emerald-900">8387238879<CopyBtn text="8387238879" label="Account number" /></span></div>
                    <div className="flex justify-between"><span className="text-emerald-600">Routing #</span><span className="font-mono font-semibold text-emerald-900">063107513<CopyBtn text="063107513" label="Routing number" /></span></div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Submit Deposit Reference */}
            {(!confirmation || confirmation.status === "rejected") && (
              <Card className="sag-card" data-testid="submit-reference-card">
                <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Hash className="h-5 w-5" /> Submit Deposit Reference / Confirmation</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-slate-500">After completing your direct deposit, enter your transaction reference number below so we can verify your payment and activate services.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="refNum">Reference / Confirmation Number *</Label>
                      <Input id="refNum" value={refNumber} onChange={(e) => setRefNumber(e.target.value)} placeholder="e.g., TXN-123456" className="mt-1" data-testid="ref-number-input" />
                    </div>
                    <div>
                      <Label htmlFor="depDate">Date of Deposit *</Label>
                      <Input id="depDate" type="date" value={depositDate} onChange={(e) => setDepositDate(e.target.value)} className="mt-1" data-testid="deposit-date-input" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="depFile">Receipt / Screenshot (optional)</Label>
                    <Input id="depFile" type="file" accept="image/png,image/jpeg,image/webp,application/pdf" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="depNotes">Additional Notes</Label>
                    <Textarea id="depNotes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any additional details..." className="mt-1" rows={2} />
                  </div>
                  <Button
                    onClick={handleSubmitReference}
                    disabled={submitting || !refNumber.trim() || !depositDate}
                    className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 font-semibold"
                    data-testid="submit-reference-btn"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                    {submitting ? "Submitting..." : "Submit Payment Reference"}
                  </Button>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
